import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useAuth } from '../../auth/AuthContext'

const LAYOUT_API_BASE = import.meta.env.DEV ? '/api' : 'https://asia-northeast1-bluepaper2.cloudfunctions.net/api'
import { OrganizationSwitcher } from '../organization/OrganizationSwitcher'
import { FolderSidebar } from '../folders/FolderSidebar'
import { IosInstallHint } from './IosInstallHint'

interface LayoutProps {
  children: ReactNode
  title?: string
  showBackButton?: boolean
  backPath?: string
  showSidebar?: boolean
  selectedFolderId?: string | null
  onFolderSelect?: (folderId: string | null) => void
  headerActions?: ReactNode
  /** ヘッダータイトル上に表示するパンくずリスト */
  breadcrumbs?: { label: string; to?: string }[]
  /** 指定するとヘッダータイトルがクリックで直接編集可能になる */
  onTitleSave?: (title: string) => void | Promise<void>
}

function NavIcon({ d }: { d: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d={d} />
    </svg>
  )
}

/** 新規議事録作成の入力方法一覧（PCドロップダウンとモバイル＋シートで共用） */
const NEW_MEETING_ITEMS = [
  {
    label: '録音する',
    desc: 'オフラインでも録音できます',
    icon: 'M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3zM19 10v1a7 7 0 0 1-14 0v-1M12 18v4',
    path: '/record'
  },
  {
    label: 'Botを会議に招待',
    desc: '進行中の会議にBotを参加させる',
    icon: 'M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71',
    path: '/?focus=bot-url'
  },
  {
    label: 'ファイルから取り込み',
    desc: '音声・動画ファイルをアップロード',
    icon: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12',
    path: '/create?method=upload'
  },
  {
    label: 'テキストから作成',
    desc: '文字起こしを貼り付けて作成',
    icon: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zM14 2v6h6M9 13h6M9 17h6',
    path: '/create?method=text'
  },
  {
    label: 'ドキュメントを作成',
    desc: 'Notion風エディタで直接書く',
    icon: 'M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z',
    path: '/create?method=note'
  }
]

/** サイドバー最上部の「新規議事録作成」ドロップダウン（Circlebackの New meeting 相当） */
function NewMeetingButton() {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onClickOutside = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [open])

  const go = (path: string) => {
    setOpen(false)
    navigate(path)
  }

  const items = NEW_MEETING_ITEMS.map((item) => ({ ...item, onClick: () => go(item.path) }))

  return (
    <div ref={wrapRef} style={{ position: 'relative', padding: 'var(--space-3) var(--space-3) 0' }}>
      <button
        className="btn btn-primary w-full"
        onClick={() => setOpen(!open)}
        style={{ justifyContent: 'space-between' }}
      >
        <span className="flex items-center gap-2">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16 }}>
            <path d="M12 5v14M5 12h14" />
          </svg>
          新規議事録作成
        </span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14, transform: open ? 'rotate(180deg)' : undefined, transition: 'transform .15s' }}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div className="dropdown-menu open" style={{ position: 'absolute', left: 'var(--space-3)', right: 'var(--space-3)', top: '100%', marginTop: 4, zIndex: 50 }}>
          {items.map((item) => (
            <button key={item.label} className="dropdown-item w-full text-left" onClick={item.onClick} style={{ alignItems: 'flex-start', gap: 10, padding: '10px 12px' }}>
              <span className="icon-wrap icon-wrap-md" style={{ flexShrink: 0 }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 15, height: 15 }}>
                  <path d={item.icon} />
                </svg>
              </span>
              <span>
                <span className="block text-sm font-medium text-primary">{item.label}</span>
                <span className="block text-xs text-secondary">{item.desc}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/** モバイル下部タブ中央の「＋」から開く新規作成ボトムシート（PCドロップダウンと同じ選択肢） */
function MobileCreateSheet({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate()
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="text-md font-semibold">新規議事録作成</h3>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>閉じる</button>
        </div>
        <div className="flex flex-col" style={{ padding: '4px 0 8px' }}>
          {NEW_MEETING_ITEMS.map((item) => (
            <button
              key={item.label}
              className="dropdown-item w-full text-left"
              onClick={() => { onClose(); navigate(item.path) }}
              style={{ alignItems: 'flex-start', gap: 10, padding: '10px 12px' }}
            >
              <span className="icon-wrap icon-wrap-md" style={{ flexShrink: 0 }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 15, height: 15 }}>
                  <path d={item.icon} />
                </svg>
              </span>
              <span>
                <span className="block text-sm font-medium text-primary">{item.label}</span>
                <span className="block text-xs text-secondary">{item.desc}</span>
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

interface AppNotification {
  id: string
  type: 'task_assigned' | 'mention' | 'task_completed'
  title: string
  body: string
  link: string
  read: boolean
  actorName?: string | null
  createdAt: string | null
}

function notificationIcon(type: AppNotification['type']): string {
  if (type === 'mention') return '💬'
  if (type === 'task_completed') return '✅'
  return '📋'
}

function timeAgo(iso: string | null): string {
  if (!iso) return ''
  const diff = Date.now() - new Date(iso).getTime()
  const min = Math.floor(diff / 60000)
  if (min < 1) return 'たった今'
  if (min < 60) return `${min}分前`
  const hour = Math.floor(min / 60)
  if (hour < 24) return `${hour}時間前`
  return `${Math.floor(hour / 24)}日前`
}

/** ヘッダーの通知ベル（60秒ポーリング） */
function NotificationBell() {
  const navigate = useNavigate()
  const { firebaseUser } = useAuth()
  const [open, setOpen] = useState(false)
  const [notifications, setNotifications] = useState<AppNotification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const wrapRef = useRef<HTMLDivElement>(null)

  const authFetch = useCallback(async (path: string, init?: RequestInit) => {
    if (!firebaseUser) throw new Error('未認証です')
    const token = await firebaseUser.getIdToken()
    return fetch(`${LAYOUT_API_BASE}${path}`, {
      ...init,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...(init?.headers || {})
      }
    })
  }, [firebaseUser])

  const fetchNotifications = useCallback(async () => {
    if (!firebaseUser) return
    try {
      const res = await authFetch('/v1/notifications')
      if (!res.ok) return
      const data = await res.json()
      setNotifications(data.notifications || [])
      setUnreadCount(typeof data.unreadCount === 'number' ? data.unreadCount : 0)
    } catch {
      // ベストエフォート
    }
  }, [firebaseUser, authFetch])

  useEffect(() => {
    fetchNotifications()
    const timer = setInterval(fetchNotifications, 60_000)
    return () => clearInterval(timer)
  }, [fetchNotifications])

  useEffect(() => {
    if (!open) return
    const onClickOutside = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [open])

  const markRead = async (ids: string[]) => {
    if (ids.length === 0) return
    setNotifications((prev) => prev.map((n) => (ids.includes(n.id) ? { ...n, read: true } : n)))
    setUnreadCount((prev) => Math.max(0, prev - ids.length))
    try {
      await authFetch('/v1/notifications/read', { method: 'POST', body: JSON.stringify({ ids }) })
    } catch {
      // ベストエフォート
    }
  }

  const handleClick = (n: AppNotification) => {
    setOpen(false)
    if (!n.read) markRead([n.id])
    navigate(n.link || '/tasks')
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button className="btn-icon borderless" aria-label="通知" onClick={() => setOpen(!open)} style={{ position: 'relative' }}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18 }}>
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unreadCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: -2,
              right: -4,
              background: '#dc2626',
              color: '#fff',
              borderRadius: 999,
              fontSize: 10,
              lineHeight: '14px',
              minWidth: 16,
              textAlign: 'center',
              padding: '0 4px',
              fontWeight: 600
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
      {open && (
        <div className="dropdown-menu open" style={{ position: 'absolute', right: 0, top: '100%', marginTop: 6, zIndex: 60, width: 340, maxHeight: 420, overflowY: 'auto' }}>
          <div className="flex items-center justify-between" style={{ padding: '8px 12px', borderBottom: '1px solid var(--color-border-light)' }}>
            <span className="text-sm font-medium">通知</span>
            <button
              className="btn btn-ghost btn-sm text-xs"
              disabled={unreadCount === 0}
              onClick={() => markRead(notifications.filter((n) => !n.read).map((n) => n.id))}
            >
              すべて既読
            </button>
          </div>
          {notifications.length === 0 ? (
            <div className="text-xs text-secondary" style={{ padding: '20px 12px', textAlign: 'center' }}>通知はありません</div>
          ) : (
            notifications.map((n) => (
              <button
                key={n.id}
                className="dropdown-item w-full text-left"
                onClick={() => handleClick(n)}
                style={{ alignItems: 'flex-start', gap: 8, padding: '10px 12px', opacity: n.read ? 0.65 : 1 }}
              >
                <span style={{ flexShrink: 0 }}>{notificationIcon(n.type)}</span>
                <span style={{ minWidth: 0 }}>
                  <span className="block text-sm" style={{ fontWeight: n.read ? 400 : 600 }}>{n.body || n.title}</span>
                  <span className="block text-xs text-secondary">{timeAgo(n.createdAt)}</span>
                </span>
                {!n.read && (
                  <span style={{ marginLeft: 'auto', flexShrink: 0, width: 8, height: 8, borderRadius: '50%', background: 'var(--color-accent, #2563eb)', marginTop: 5 }} />
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Blueprint UI Kit canonical app shell:
 * <sidebar> + <#main-view> ( .top-header + .content-scroll > .content-pad )
 */
export function AppLayout({
  children,
  title,
  selectedFolderId = null,
  onFolderSelect,
  headerActions,
  breadcrumbs,
  onTitleSave,
}: LayoutProps) {
  const navigate = useNavigate()
  const { user, logout, firebaseUser } = useAuth()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  // デスクトップでのサイドバー折りたたみ（永続化）
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('bp-sidebar-collapsed') === '1' } catch { return false }
  })
  const toggleSidebar = () => {
    if (window.matchMedia('(min-width: 901px)').matches) {
      setSidebarCollapsed((v) => {
        try { localStorage.setItem('bp-sidebar-collapsed', v ? '' : '1') } catch { /* ignore */ }
        return !v
      })
    } else {
      setSidebarOpen((v) => !v)
    }
  }
  const [accountMenuOpen, setAccountMenuOpen] = useState(false)
  const [createSheetOpen, setCreateSheetOpen] = useState(false)
  // ヘッダータイトルのインライン編集（onTitleSave指定時のみ有効）
  const [titleEditing, setTitleEditing] = useState(false)
  const [titleDraft, setTitleDraft] = useState('')
  const commitTitle = () => {
    setTitleEditing(false)
    const next = titleDraft.trim()
    if (onTitleSave && next && next !== title) onTitleSave(next)
  }
  const closeSidebar = () => setSidebarOpen(false)
  const [pendingKnowledgeCount, setPendingKnowledgeCount] = useState(0)

  const fetchPendingCount = useCallback(async () => {
    if (!firebaseUser) return
    try {
      const token = await firebaseUser.getIdToken()
      const res = await fetch(`${LAYOUT_API_BASE}/v1/knowledge/candidates`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!res.ok) return
      const data = await res.json()
      setPendingKnowledgeCount(typeof data.total === 'number' ? data.total : (data.candidates || []).length)
    } catch {
      // ベストエフォート（バッジ非表示のまま）
    }
  }, [firebaseUser])

  useEffect(() => {
    fetchPendingCount()
  }, [fetchPendingCount])

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/login')
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  const initial = (user?.displayName || user?.email || 'U').charAt(0).toUpperCase()

  return (
    <div className="flex h-screen overflow-hidden w-full flex-1">
      {/* ── Sidebar ── */}
      <aside className={`sidebar${sidebarOpen ? ' open' : ''}${sidebarCollapsed ? ' desktop-collapsed' : ''}`}>
        <div className="brand-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <button onClick={() => { closeSidebar(); navigate('/') }} className="flex items-center bg-transparent border-0 cursor-pointer p-0">
            <span className="brand-logo" style={{ fontFamily: "var(--font-brand)", fontSize: 26, fontWeight: 700, letterSpacing: "0.02em" }}>BluePaper</span>
          </button>
          {/* サイドバーを閉じる（デスクトップ: 折りたたみ / モバイル: ドロワーを閉じる） */}
          <button
            className="btn-icon borderless"
            onClick={toggleSidebar}
            title="サイドバーを閉じる"
            aria-label="サイドバーを閉じる"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18 }}>
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <path d="M9 3v18M15 10l-2 2 2 2" />
            </svg>
          </button>
        </div>

        {/* Circleback式: 新規議事録作成のドロップダウンボタン */}
        <NewMeetingButton />

        <nav className="nav-section" aria-label="メインナビゲーション" onClick={closeSidebar}>
          <div className="nav-label">ワークスペース</div>
          <NavLink to="/" end className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
            <NavIcon d="M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
            会議予定
          </NavLink>
          <NavLink to="/documents" className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
            <NavIcon d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zM14 2v6h6M9 13h6M9 17h6" />
            議事録
          </NavLink>
          <NavLink to="/tasks" className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
            {/* チェックリストアイコン */}
            <NavIcon d="M3 6l2 2 4-4M3 12l2 2 4-4M3 18l2 2 4-4M13 6h8M13 12h8M13 18h8" />
            TODO
          </NavLink>
          <NavLink to="/people" end className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
            {/* 人物アイコン */}
            <NavIcon d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
            人物
          </NavLink>
          <NavLink to="/context" className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
            {/* brain/databaseアイコン */}
            <NavIcon d="M12 2a4 4 0 0 0-4 4v1a4 4 0 0 0-2 7.5A4 4 0 0 0 8 21h8a4 4 0 0 0 2-6.5A4 4 0 0 0 16 7V6a4 4 0 0 0-4-4zM12 2v19M8 9h.01M16 13h.01" />
            <span className="flex items-center gap-2" style={{ flex: 1, justifyContent: 'space-between' }}>
              コンテキスト
              {pendingKnowledgeCount > 0 && (
                <span
                  className="badge"
                  style={{
                    background: 'var(--color-accent, #2563eb)',
                    color: '#fff',
                    borderRadius: 999,
                    fontSize: 11,
                    lineHeight: '16px',
                    minWidth: 18,
                    textAlign: 'center',
                    padding: '0 6px'
                  }}
                >
                  {pendingKnowledgeCount}
                </span>
              )}
            </span>
          </NavLink>
        </nav>

        {/* フォルダナビゲーション（全ページで常時表示。
            議事録ページ以外ではフォルダ選択時に /folder/:id へ遷移する） */}
        <div className="flex-1 min-h-0 overflow-y-auto scrollable">
          <FolderSidebar
            selectedFolderId={selectedFolderId}
            onFolderSelect={
              onFolderSelect ||
              ((folderId: string | null) => {
                closeSidebar()
                navigate(folderId && folderId !== 'bookmarks' ? `/folder/${folderId}` : '/documents')
              })
            }
          />
        </div>

      </aside>

      {/* モバイル用: サイドバー背後のオーバーレイ */}
      <div className="sidebar-overlay" onClick={closeSidebar} aria-hidden="true" />

      {/* ── Main View ── */}
      <div id="main-view">
        <header className="top-header">
          <button
            className={`menu-toggle btn-icon borderless${sidebarCollapsed ? '' : ' desktop-hidden'}`}
            onClick={toggleSidebar}
            aria-label="サイドバーを開閉"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 20, height: 20 }}>
              <path d="M3 6h18M3 12h18M3 18h18" />
            </svg>
          </button>
          <div className="flex flex-col" style={{ minWidth: 0, flex: 1, justifyContent: 'center' }}>
            {breadcrumbs && breadcrumbs.length > 0 && (
              <nav className="text-xs text-secondary truncate" aria-label="パンくずリスト" style={{ lineHeight: 1.4 }}>
                {breadcrumbs.map((bc, i) => (
                  <span key={i}>
                    {i > 0 && <span style={{ margin: '0 4px' }}>/</span>}
                    {bc.to ? (
                      <NavLink to={bc.to} style={{ color: 'inherit' }}>{bc.label}</NavLink>
                    ) : (
                      <span>{bc.label}</span>
                    )}
                  </span>
                ))}
              </nav>
            )}
            {onTitleSave && titleEditing ? (
              <input
                type="text"
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                onBlur={commitTitle}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitTitle()
                  if (e.key === 'Escape') setTitleEditing(false)
                }}
                className="form-input"
                style={{ fontSize: 18, fontWeight: 600, padding: '2px 8px', maxWidth: 560 }}
                autoFocus
              />
            ) : (
              <h1
                className="truncate"
                onClick={onTitleSave ? () => { setTitleDraft(title || ''); setTitleEditing(true) } : undefined}
                style={onTitleSave ? { cursor: 'pointer' } : undefined}
                title={onTitleSave ? 'クリックしてタイトルを編集' : undefined}
              >
                {title || 'BluePaper'}
              </h1>
            )}
          </div>
          <div className="header-actions">
            {headerActions}
            <NotificationBell />
            {/* アカウントメニュー: 名前だけ表示し、クリックで組織切替/設定/ログアウトを開く */}
            <div className="relative" style={{ display: 'inline-block' }}>
              <button
                className="btn btn-ghost btn-sm flex items-center gap-2"
                onClick={() => setAccountMenuOpen(!accountMenuOpen)}
                title={user?.email || ''}
              >
                <span className="avatar avatar-accent">{initial}</span>
                <span className="text-sm truncate hidden sm:block" style={{ maxWidth: 160 }}>
                  {user?.displayName || user?.email || 'ユーザー'}
                </span>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14, transform: accountMenuOpen ? 'rotate(180deg)' : undefined, transition: 'transform 0.15s' }}>
                  <path d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {accountMenuOpen && (
                <div className="fixed inset-0" style={{ zIndex: 10 }} onClick={() => setAccountMenuOpen(false)} />
              )}
              <div className={`dropdown-menu right${accountMenuOpen ? ' open' : ''}`} style={{ width: 260 }}>
                <div className="dropdown-menu-label" title={user?.email || ''}>
                  {user?.email || 'ユーザー'}
                </div>
                <div style={{ padding: '2px 8px' }}>
                  <OrganizationSwitcher />
                </div>
                <button
                  className="dropdown-item w-full text-left flex items-center gap-2"
                  onClick={() => { setAccountMenuOpen(false); navigate('/settings') }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 15, height: 15 }}>
                    <circle cx="12" cy="12" r="3" />
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                  </svg>
                  設定
                </button>
                {user?.email?.endsWith('@bp-g.jp') && (
                  <button
                    className="dropdown-item w-full text-left flex items-center gap-2"
                    onClick={() => { setAccountMenuOpen(false); navigate('/ops') }}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 15, height: 15 }}>
                      <path d="M12 2l9 4.9V17L12 22l-9-4.9V7z" />
                      <path d="M12 22V12M21 7l-9 5-9-5" />
                    </svg>
                    運営管理
                  </button>
                )}
                <button
                  className="dropdown-item w-full text-left flex items-center gap-2"
                  onClick={() => { setAccountMenuOpen(false); handleLogout() }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 15, height: 15 }}>
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
                  </svg>
                  ログアウト
                </button>
              </div>
            </div>
          </div>
        </header>

        <div className="content-scroll">
          <div className="content-pad">{children}</div>
        </div>

        <IosInstallHint />

        {/* モバイル専用: 下部タブバー（Circleback風） */}
        <nav className="mobile-tabbar" aria-label="モバイルナビゲーション">
          <NavLink to="/" end className={({ isActive }) => `mobile-tab${isActive ? ' active' : ''}`} aria-label="会議予定">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
            </svg>
          </NavLink>
          <NavLink to="/documents" className={({ isActive }) => `mobile-tab${isActive ? ' active' : ''}`} aria-label="議事録">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zM14 2v6h6M9 13h6M9 17h6" />
            </svg>
          </NavLink>
          <button
            type="button"
            className="mobile-tab mobile-tab-record"
            aria-label="新規議事録作成"
            onClick={() => setCreateSheetOpen(true)}
          >
            <span className="mobile-tab-record-circle">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 5v14M5 12h14" />
              </svg>
            </span>
          </button>
          <NavLink to="/record" className={({ isActive }) => `mobile-tab${isActive ? ' active' : ''}`} aria-label="録音">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3zM19 10v1a7 7 0 0 1-14 0v-1M12 18v4" />
            </svg>
          </NavLink>
          <NavLink to="/tasks" className={({ isActive }) => `mobile-tab${isActive ? ' active' : ''}`} aria-label="TODO">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <path d="M22 4L12 14.01l-3-3" />
            </svg>
          </NavLink>
        </nav>

        {createSheetOpen && <MobileCreateSheet onClose={() => setCreateSheetOpen(false)} />}
      </div>
    </div>
  )
}

/** 後方互換エイリアス（既存ページは <Layout> を使用） */
export const Layout = AppLayout
